// src/components/ChatBot/ChatBot.jsx
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Badge,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
  Chip,
  alpha,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  LinearProgress,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import {
  ChatBubbleOutline,
  Close,
  Send,
  SmartToy,
  Person,
  HelpOutline,
  Settings,
  Delete,
  Stream,
} from "@mui/icons-material";

// Импортирай всички actions и thunks
import {
  addMessage,
  sendChatMessage,
  sendChatMessageStream,
  clearChat,
  toggleChat,
  closeChat,
  setMode,
  setUseStreaming,
} from "../../store/slices/chatSlice";

// Анимации
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const drawerSlideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const drawerSlideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const messageSlideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const messageSlideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ChatBot = () => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  
  // Refs за автоматично скролиране
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Redux hooks
  const dispatch = useDispatch();
  const {
    messages,
    loading,
    streaming,
    isOpen,
    mode,
    useStreaming,
    currentConversationId,
    streamingMessage,
  } = useSelector((state) => state.chat);

  // Ефект за автоматично скролиране
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, loading]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      requestAnimationFrame(() => {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      });
    }
  };

  const fabPulseOrange = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(255, 87, 34, 0.7);
    }
    70% {
      box-shadow: 0 0 0 15px rgba(255, 87, 34, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 87, 34, 0);
    }
  `;

  const fabPulseOrangeHover = keyframes`
    0% {
      box-shadow: 
        0px 4px 6px -2px rgba(255, 87, 34, 0.3),
        0px 8px 12px 1px rgba(255, 87, 34, 0.2),
        0 0 0 0 rgba(255, 87, 34, 0.8);
    }
    50% {
      box-shadow: 
        0px 6px 10px -3px rgba(255, 87, 34, 0.4),
        0px 12px 18px 2px rgba(255, 87, 34, 0.3),
        0 0 0 15px rgba(255, 87, 34, 0.1);
    }
    100% {
      box-shadow: 
        0px 4px 6px -2px rgba(255, 87, 34, 0.3),
        0px 8px 12px 1px rgba(255, 87, 34, 0.2),
        0 0 0 0 rgba(255, 87, 34, 0.8);
    }
  `;

  const predefinedQuestions = [
    "Може ли читалищата да имат собствени приходи?",
    "В колко града от област Враца има читалища?",
    "Кои са читалищата с най-много служители в област Русе?",
  ];

  const modeOptions = [
    { value: "low", label: "Нисък", description: "Строги факти" },
    { value: "medium", label: "Среден", description: "Балансиран" },
    { value: "high", label: "Висок", description: "Креативен" },
  ];

  const handleSendMessage = async () => {
    if (!message.trim() || loading) return;

    // Добавяне на потребителското съобщение
    dispatch(
      addMessage({
        text: message,
        sender: "user",
      })
    );

    const userMessage = message;
    setMessage("");

    // Избор между streaming и нормален endpoint
    if (useStreaming) {
      dispatch(
        sendChatMessageStream({
          message: userMessage,
          conversation_id: currentConversationId,
          mode,
        })
      );
    } else {
      dispatch(
        sendChatMessage({
          message: userMessage,
          conversation_id: currentConversationId,
          mode,
        })
      );
    }
  };

  const handleQuickQuestion = (question) => {
    // Добавяне на потребителското съобщение
    dispatch(
      addMessage({
        text: question,
        sender: "user",
      })
    );

    // Избор между streaming и нормален endpoint
    if (useStreaming) {
      dispatch(
        sendChatMessageStream({
          message: question,
          conversation_id: currentConversationId,
          mode,
        })
      );
    } else {
      dispatch(
        sendChatMessage({
          message: question,
          conversation_id: currentConversationId,
          mode,
        })
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    dispatch(clearChat());
  };

  const handleModeChange = (event) => {
    dispatch(setMode(event.target.value));
  };

  const handleStreamingToggle = (event) => {
    dispatch(setUseStreaming(event.target.checked));
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString("bg-BG", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <>
      {/* Плаващ бутон за чат */}
      <Fab
        aria-label="чат"
        onClick={() => dispatch(toggleChat())}
        sx={{
          backgroundColor: theme.palette.custom.primaryOrange,
          color: "white",
          position: "fixed",
          bottom: 34,
          right: 24,
          zIndex: 1000,
          animation: `
            ${fadeIn} 0.5s ease-out,
            ${fabPulseOrange} 3s infinite 2s
          `,
          boxShadow: 4,
          "&:hover": {
            backgroundColor: "#ffffff",
            color: theme.palette.custom.primaryOrange,
            transform: "scale(1.1)",
            boxShadow: 6,
            animation: `
              ${fadeIn} 0.5s ease-out,
              ${fabPulseOrangeHover} 1.5s infinite
            `,
          },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Badge
          color="error"
          variant="dot"
          invisible={messages.length <= 1}
          sx={{
            "& .MuiBadge-badge": {
              animation: `${fadeIn} 0.5s ease-out`,
              backgroundColor: "#FF3D00",
            },
            "& .MuiSvgIcon-root": {
              transition: "color 0.3s ease",
            },
          }}
        >
          <ChatBubbleOutline />
        </Badge>
      </Fab>

      {/* Чат панел */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => dispatch(closeChat())}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 450 },
            maxWidth: "100%",
            animation: isOpen
              ? `${drawerSlideIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards`
              : `${drawerSlideOut} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
        transitionDuration={300}
      >
        {/* Заглавие на чата */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${(theme) => theme.palette.divider}`,
            backgroundColor: "primary.main",
            color: "white",
            animation: `${fadeIn} 0.4s ease-out 0.1s both`,
            borderRadius: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                bgcolor: "white",
                color: "primary.main",
                animation: `${fadeIn} 0.5s ease-out 0.2s both`,
              }}
            >
              <SmartToy />
            </Avatar>
            <Box sx={{ animation: `${fadeIn} 0.5s ease-out 0.3s both` }}>
              <Typography variant="h6" fontWeight={600}>
                Чат помощник
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {loading ? "Отговаря..." : "Готов"}
                </Typography>
                {streaming && (
                  <Chip
                    label="Стрийминг"
                    size="small"
                    color="warning"
                    icon={<Stream sx={{ fontSize: 14 }} />}
                    sx={{ height: 20, fontSize: "0.6rem" }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Настройки">
              <IconButton
                onClick={() => setShowSettings(!showSettings)}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Settings />
              </IconButton>
            </Tooltip>
            <Tooltip title="Затвори">
              <IconButton
                onClick={() => dispatch(closeChat())}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transform: "rotate(90deg)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Настройки панел */}
        {showSettings && (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              m: 2,
              borderRadius: 2,
              animation: `${fadeIn} 0.3s ease-out`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Настройки на чата
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Режим на отговор</InputLabel>
              <Select
                value={mode}
                label="Режим на отговор"
                onChange={handleModeChange}
              >
                {modeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* <FormControlLabel
              control={
                <Switch
                  checked={useStreaming}
                  onChange={handleStreamingToggle}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Stream sx={{ fontSize: 16 }} />
                  <Typography variant="body2">Streaming отговор</Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            /> */}

            <Button
              variant="outlined"
              size="small"
              startIcon={<Delete />}
              onClick={handleClearChat}
              disabled={loading}
              fullWidth
            >
              Изчисти чат
            </Button>
          </Paper>
        )}

        {/* Съобщения */}
        <Box
          ref={messagesContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: (theme) => alpha(theme.palette.grey[300], 0.3),
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: (theme) => alpha(theme.palette.primary.main, 0.3),
              borderRadius: "3px",
              "&:hover": {
                background: (theme) => alpha(theme.palette.primary.main, 0.5),
              },
            },
            scrollBehavior: "smooth",
          }}
        >
          <List sx={{ width: "100%" }}>
            {messages.map((msg, index) => (
              <React.Fragment key={msg.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection:
                      msg.sender === "user" ? "row-reverse" : "row",
                    px: 0,
                    animation:
                      msg.sender === "user"
                        ? `${messageSlideInRight} 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${
                            index * 0.05
                          }s both`
                        : `${messageSlideInLeft} 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${
                            index * 0.05
                          }s both`,
                  }}
                >
                  <ListItemAvatar
                    sx={{
                      minWidth: 40,
                      ml: msg.sender === "user" ? 1 : 0,
                      mr: msg.sender === "user" ? 0 : 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor:
                          msg.sender === "bot"
                            ? "primary.main"
                            : "secondary.main",
                        width: 32,
                        height: 32,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {msg.sender === "bot" ? <SmartToy /> : <Person />}
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ maxWidth: "80%" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: (theme) =>
                          msg.sender === "bot"
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.secondary.main, 0.1),
                        borderRadius: 2,
                        borderTopLeftRadius: msg.sender === "user" ? 12 : 2,
                        borderTopRightRadius: msg.sender === "user" ? 2 : 12,
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: 1,
                        },
                      }}
                    >
                      <Typography variant="body2">{msg.text}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          opacity: 0.6,
                          textAlign: msg.sender === "user" ? "right" : "left",
                        }}
                      >
                        {formatTimestamp(msg.timestamp)}
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
                {msg.id < messages.length && (
                  <Divider
                    variant="inset"
                    component="li"
                    sx={{
                      animation: `${fadeIn} 0.3s ease-out ${
                        index * 0.05 + 0.2
                      }s both`,
                    }}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Streaming съобщение */}
            {streamingMessage && (
              <ListItem
                alignItems="flex-start"
                sx={{
                  px: 0,
                  animation: `${messageSlideInLeft} 0.3s cubic-bezier(0.4, 0, 0.2, 1) both`,
                }}
              >
                <ListItemAvatar sx={{ minWidth: 40, mr: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: "warning.main",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <Stream />
                  </Avatar>
                </ListItemAvatar>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    maxWidth: "80%",
                    backgroundColor: (theme) =>
                      alpha(theme.palette.warning.main, 0.1),
                    borderRadius: 2,
                    borderTopRightRadius: 12,
                  }}
                >
                  <Typography variant="body2">
                    {streamingMessage}
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 8,
                        height: 16,
                        bgcolor: "primary.main",
                        animation: `${fadeIn} 1s infinite`,
                        verticalAlign: "middle",
                        ml: 0.5,
                      }}
                    />
                  </Typography>
                </Paper>
              </ListItem>
            )}

            {/* Индикатор за зареждане */}
            {loading && !streaming && !streamingMessage && (
              <ListItem
                alignItems="flex-start"
                sx={{
                  px: 0,
                  animation: `${messageSlideInLeft} 0.3s cubic-bezier(0.4, 0, 0.2, 1) both`,
                }}
              >
                <ListItemAvatar sx={{ minWidth: 40, mr: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <SmartToy />
                  </Avatar>
                </ListItemAvatar>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    maxWidth: "80%",
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 2,
                    borderTopRightRadius: 12,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress sx={{ flex: 1, height: 4 }} />
                    <Typography variant="caption" color="text.secondary">
                      Обработка...
                    </Typography>
                  </Box>
                </Paper>
              </ListItem>
            )}
            
            <div ref={messagesEndRef} />
          </List>
        </Box>

        {/* Бързи въпроси */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${(theme) => theme.palette.divider}`,
            animation: `${fadeIn} 0.4s ease-out 0.4s both`,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Бързи въпроси:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {predefinedQuestions.map((q, index) => (
              <Chip
                key={index}
                label={q}
                size="small"
                onClick={() => handleQuickQuestion(q)}
                disabled={loading}
                icon={<HelpOutline sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: "0.7rem",
                  cursor: "pointer",
                  animation: `${fadeIn} 0.3s ease-out ${
                    0.5 + index * 0.1
                  }s both`,
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.1),
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:disabled": {
                    opacity: 0.5,
                    transform: "none",
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Поле за въвеждане */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${(theme) => theme.palette.divider}`,
            animation: `${fadeIn} 0.4s ease-out 0.5s both`,
          }}
        >
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Задайте въпрос за читалищните данни..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              maxRows={3}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: 1,
                  },
                  "&.Mui-focused": {
                    boxShadow: (theme) =>
                      `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!message.trim() || loading}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark",
                  transform: "scale(1.05)",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  transform: "none",
                },
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Send />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Натиснете Enter за изпращане, Shift+Enter за нов ред
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default ChatBot;